import { useParams } from "react-router-dom";

import { BasecampContainer } from "layouts/OnboardingLayout/OnboardingLayout";
import FormLoader from "components/FormLoader";
import FormV2 from "components/FormV2/FormV2";
import Header from "components/Header";
import { TASK_NAMES } from "hooks/useCaseTasksAPI";
import { setTaskOptimism } from "helpers/taskOptimism";

// Case-scoped forms whose submission completes a Task System task. For
// these forms the route's :responseId is the caseId.
const TASK_BY_FORM_ID: Record<string, string> = {
  vitals: TASK_NAMES.collectVitals,
};

// The v1 engine serves exactly these ids; anything else on the shared
// /forms route is a Forms v2 form, where :formId is the definition ref and
// :responseId is the grant token from the emailed/SMS'd link.
const V1_FORM_IDS = new Set(["vitals", "pre-need-application", "dev_form_123"]);

export default function Form() {
  const { formId, responseId } = useParams<{
    formId: string;
    responseId: string;
  }>();
  const taskName = formId ? TASK_BY_FORM_ID[formId] : undefined;
  const handleSubmitted =
    taskName && responseId
      ? () => setTaskOptimism(responseId, taskName)
      : undefined;

  // v2 pages carry their own chrome: the standard header over a cream page,
  // with the form in a narrow left-aligned column.
  if (formId && !V1_FORM_IDS.has(formId) && responseId) {
    // Flow-less: no quote (or any other purchase flow) wraps this route,
    // so the renderer's own generic events are the whole analytics story.
    return (
      <div className="flex min-h-screen flex-col bg-[#FBF7EC]">
        <Header />
        <main className="mx-auto w-full max-w-[760px] flex-1 px-5 pb-16 pt-7 sm:px-10 sm:pb-16 sm:pt-11">
          <FormV2 formRef={formId} grantToken={responseId} />
        </main>
      </div>
    );
  }

  return (
    <BasecampContainer>
      <div className="w-full">
        {!formId && (
          <div className="text-center">
            <h1 className="mb-2 font-serif">
              Sorry, we could not find that form
            </h1>
            <p>
              Please reach out to us at{" "}
              <a
                href="mailto:support@after.com"
                target="_blank"
                className="underline decoration-dotted underline-offset-4 hover:decoration-solid"
              >
                support@after.com
              </a>{" "}
              if you need help.
            </p>
          </div>
        )}
        {formId && V1_FORM_IDS.has(formId) && (
          <FormLoader
            formId={formId}
            responseId={responseId || "test"}
            onSubmitted={handleSubmitted}
          />
        )}
      </div>
    </BasecampContainer>
  );
}
